import { Flow, Node, Edge, Handle } from '../context/FluxogramaContext';
import { deepEqual } from '../core';

/**
 * Compara dois arrays de objetos com 'id' e classifica em: criados, atualizados e removidos.
 */
function diffArrayById<T extends { id: string; }>(
  original: T[],
  updated: T[],
  isEqual: (a: T, b: T) => boolean
): {
  created: T[];
  updated: T[];
  deleted: T[];
} {
  const created: T[] = [];
  const updatedArr: T[] = [];
  const deleted: T[] = [];

  const originalMap = new Map(original.map(item => [item.id, item]));
  const updatedMap = new Map(updated.map(item => [item.id, item]));

  for (const item of updated) {
    const originalItem = originalMap.get(item.id);
    if (!originalItem) {
      created.push(item);
    } else if (!isEqual(originalItem, item)) {
      updatedArr.push(item);
    }
  }

  for (const item of original) {
    if (!updatedMap.has(item.id)) {
      deleted.push(item);
    }
  }

  return { created, updated: updatedArr, deleted };
}

export interface DiffItem<T> {
  created: T[];
  updated: T[];
  deleted: T[];
}
export interface DiffFlow {
  nodes: DiffItem<Node>;
  edges: DiffItem<Edge>;
  handles: DiffItem<Handle>;
}

/**
 * Retorna as diferenças entre os estados original e atualizado de um Flow.
 * Inclui: nodes, handles (aninhados nos nodes) e edges.
 */
export function diffFlowEntities(original: Flow, updated: Flow): DiffFlow {
  const originalHandles = original.nodes.flatMap(n => n.handles);
  const updatedHandles = updated.nodes.flatMap(n => n.handles);

  return {
    nodes: diffArrayById<Node>(
      original.nodes,
      updated.nodes,
      (a, b) => deepEqual(a, b)
    ),
    edges: diffArrayById<Edge>(
      original.edges,
      updated.edges,
      (a, b) => deepEqual(a, b)
    ),
    handles: diffArrayById<Handle>(
      originalHandles,
      updatedHandles,
      (a, b) => deepEqual(a, b)
    ),
  };
}
